import React, { useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";

const TELEGRAM_URL = "https://t.me/nmn_energy";

const NavLink = ({ href, children, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    className="text-[13px] tracking-wide text-white/70 hover:text-[#e8d4a8] transition-colors duration-300"
    data-testid={`nav-link-${href.replace("#", "")}`}
  >
    {children}
  </a>
);

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="#top" className="flex items-center gap-3" data-testid="logo-home">
          <span className="w-10 h-10 border border-[#e8d4a8]/60 flex items-center justify-center shrink-0">
            <span className="font-display text-[#e8d4a8] text-[11px] tracking-[0.15em] leading-none">
              NMN
            </span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-display text-[15px] md:text-[16px] tracking-wide text-white">
              NMN <span className="text-[#e8d4a8]">Energy Consulting</span>
            </span>
            <span className="hidden sm:block font-mono-l text-[8.5px] text-white/45 mt-1">
              {t.brand.subtitle}
            </span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          <NavLink href="#locations">{t.nav.locations}</NavLink>
          <NavLink href="#services">{t.nav.services}</NavLink>
          <NavLink href="#benefits">{t.nav.benefits}</NavLink>
          <NavLink href="#payments">{t.nav.payments}</NavLink>
          <NavLink href="#testimonials">{t.nav.testimonials}</NavLink>
          <NavLink href="#contact">{t.nav.contact}</NavLink>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Language switcher */}
          <div
            className="hidden sm:flex items-center text-[11px] tracking-[0.2em] uppercase border border-white/10 rounded-full overflow-hidden"
            data-testid="language-switcher"
          >
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en"
                  ? "bg-[#e8d4a8] text-black"
                  : "text-white/60 hover:text-white"
              }`}
              data-testid="lang-en-btn"
            >
              EN
            </button>
            <button
              onClick={() => setLang("ru")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "ru"
                  ? "bg-[#e8d4a8] text-black"
                  : "text-white/60 hover:text-white"
              }`}
              data-testid="lang-ru-btn"
            >
              RU
            </button>
          </div>

          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#e8d4a8] text-black text-[12px] tracking-[0.18em] uppercase font-medium hover:bg-white transition-colors duration-300"
            data-testid="header-cta-btn"
          >
            {t.nav.cta}
            <span aria-hidden>→</span>
          </a>

          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <div className="space-y-1.5">
              <span
                className={`block w-6 h-px bg-white transition-transform ${
                  mobileOpen ? "translate-y-2 rotate-45" : ""
                }`}
              ></span>
              <span
                className={`block w-6 h-px bg-white transition-opacity ${
                  mobileOpen ? "opacity-0" : ""
                }`}
              ></span>
              <span
                className={`block w-6 h-px bg-white transition-transform ${
                  mobileOpen ? "-translate-y-2 -rotate-45" : ""
                }`}
              ></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-out ${
          mobileOpen ? "max-h-[520px]" : "max-h-0"
        }`}
        data-testid="mobile-menu"
      >
        <div className="px-6 pb-8 pt-2 bg-black/90 backdrop-blur-xl border-b border-white/5 space-y-5">
          {[
            ["locations", t.nav.locations],
            ["services", t.nav.services],
            ["benefits", t.nav.benefits],
            ["payments", t.nav.payments],
            ["testimonials", t.nav.testimonials],
            ["contact", t.nav.contact],
          ].map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={() => setMobileOpen(false)}
              className="block text-white/80 hover:text-[#e8d4a8] text-lg font-display"
              data-testid={`mobile-nav-${id}`}
            >
              {label}
            </a>
          ))}
          <div
            className="flex items-center text-[11px] tracking-[0.2em] uppercase border border-white/10 rounded-full overflow-hidden w-fit"
          >
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 ${
                lang === "en" ? "bg-[#e8d4a8] text-black" : "text-white/60"
              }`}
              data-testid="mobile-lang-en"
            >
              EN
            </button>
            <button
              onClick={() => setLang("ru")}
              className={`px-3 py-1.5 ${
                lang === "ru" ? "bg-[#e8d4a8] text-black" : "text-white/60"
              }`}
              data-testid="mobile-lang-ru"
            >
              RU
            </button>
          </div>
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#e8d4a8] text-black text-[12px] tracking-[0.18em] uppercase font-medium"
            data-testid="mobile-cta-btn"
          >
            {t.nav.cta} →
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;
